import * as React from "react";
import { cn } from "../../lib/utils";

// Helper function to get the text utility class name
function getTextClassName(
  variant: "display" | "heading" | "body" | "caption" = "body",
  size:
    | "2xlarge"
    | "xlarge"
    | "large"
    | "medium"
    | "small"
    | "xsmall" = "medium",
  weight: "regular" | "medium" | "semibold" = "regular",
  color:
    | "default"
    | "subtle"
    | "muted"
    | "disabled"
    | "primary"
    | "onPrimary"
    | "positive"
    | "negative"
    | "notice"
    | "info"
    | "neutral" = "default"
): string {
  // Build the base class name
  let baseClass = `text-${variant}`;

  // Add size
  if (size) {
    baseClass += `-${size}`;
  }

  // Add weight
  if (weight) {
    baseClass += `-${weight}`;
  }

  // Add color class separately
  const colorClass = `text-color-${color}`;

  return `${baseClass} ${colorClass}`;
}

export interface TextProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "color"> {
  children: React.ReactNode;
  variant?: "display" | "heading" | "body" | "caption";
  size?: "2xlarge" | "xlarge" | "large" | "medium" | "small" | "xsmall";
  weight?: "regular" | "medium" | "semibold";
  color?:
    | "default"
    | "subtle"
    | "muted"
    | "disabled"
    | "primary"
    | "onPrimary"
    | "positive"
    | "negative"
    | "notice"
    | "info"
    | "neutral";
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "div" | "label";
  className?: string;
}

const Text = React.forwardRef<HTMLElement, TextProps>(
  (
    {
      className,
      variant = "body",
      size = "medium",
      weight = "regular",
      color = "default",
      as = "p",
      children,
      ...props
    },
    ref
  ) => {
    const Component = as;

    const textClass = getTextClassName(variant, size, weight, color);

    return React.createElement(
      Component,
      {
        className: cn(textClass, className),
        ref,
        ...props,
      },
      children
    );
  }
);

Text.displayName = "Text";

export { Text };
